import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpCircle,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Calendar,
  Eye
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminSupportTickets } from '../../hooks/useAdminSupportTickets';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminSupportTickets = () => {
  const navigate = useNavigate();
  const {
    tickets,
    isLoading,
    error,
    filters,
    priorityOptions,
    categoryOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    getStatusColor,
    getPriorityColor
  } = useAdminSupportTickets();

  // Define table columns
  const columns = [
    {
      key: 'id',
      label: 'Ticket ID',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className="font-mono text-sm font-medium text-primary-600">
          #{String(ticket.id).slice(0, 8)}
        </span>
      )
    },
    {
      key: 'user',
      label: 'User',
      sortable: true,
      width: 'w-48',
      render: (ticket) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {ticket.user?.name || 'N/A'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {ticket.user?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className="text-sm text-gray-900 capitalize">
          {ticket.category || 'N/A'}
        </span>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
          {ticket.priority ? ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) : 'N/A'}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
          {ticket.status ? ticket.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(ticket.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(ticket.created_at)}
            </div>
          </div>
        </div>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View & Reply',
      icon: Eye,
      variant: 'default',
      onClick: (ticket) => {
        navigate(`/admin/support-tickets/${ticket.id}`);
      }
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Support Tickets</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load support tickets. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <HelpCircle className="w-6 h-6" />
                Support Tickets
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage and respond to customer support tickets
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_tickets || 0}</p>
              </div>
              <HelpCircle className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.open_tickets || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.resolved_tickets || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.high_priority_tickets || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4 mb-4">
            {[
              { value: '', label: 'All', count: stats.total_tickets || 0 },
              { value: 'open', label: 'Open', count: stats.by_status?.open || 0 },
              { value: 'in_progress', label: 'In Progress', count: stats.by_status?.in_progress || 0 },
              { value: 'resolved', label: 'Resolved', count: stats.resolved_tickets || 0 },
              { value: 'closed', label: 'Closed', count: stats.closed_tickets || 0 }
            ].map((tab) => (
              <button
                key={tab.value || 'all'}
                onClick={() => handleFilterChange('status', tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filters.status === tab.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <SearchSelect
              label="Priority"
              value={filters.priority}
              onChange={(value) => handleFilterChange('priority', value)}
              options={priorityOptions}
            />
            <SearchSelect
              label="Category"
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
              options={categoryOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={tickets}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by subject, description, user, or ticket number..."
            onSearch={handleSearch}
            emptyMessage="No support tickets found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTickets;

